<script setup lang="ts">
import {
    ref, computed, nextTick, watch, onMounted,
} from 'vue';
import { ChipInputContainer, BChip } from '@/design-system';
import { useTranslation } from '@/composables/useTranslation';
import ConditionFieldChip from '../base/condition-chips/ConditionFieldChip.vue';
import ConditionOperatorChip from '../base/condition-chips/ConditionOperatorChip.vue';
import ConditionValueChip from '../base/condition-chips/ConditionValueChip.vue';
import ConditionDateRangeValueChip from '../base/condition-chips/ConditionDateRangeValueChip.vue';
import IntelligentFilterInput from '../base/condition-chips/IntelligentFilterInput.vue';
import type { FilterConditionType } from '@/types/FilterConditionTypes';
import type { FilterFieldMetadata } from '@/types/FilterMetadataTypes';
import type { ConditionFieldType } from '@/types/HookConditionTypes';
import { formatDateForDisplay } from '@/utils/dateUtils';

type ConditionStepType = 'field' | 'operator' | 'value';

const props = withDefaults(
    defineProps<{
        fields?: FilterFieldMetadata[];
        fetchValueSuggestions?: (field: string, autocomplete?: string, query?: string) => Promise<string[]>;
        valueSuggestions?: string[];
        loading?: boolean;
        suggestionsLoading?: boolean;
        disabled?: boolean;
        searchPlaceholder?: string;
    }>(),
    {
        fields: () => [],
        valueSuggestions: () => [],
        loading: false,
        suggestionsLoading: false,
        disabled: false,
        searchPlaceholder: '',
    },
);

const { t } = useTranslation();

const conditions = defineModel<FilterConditionType[]>({ default: () => [] });
const searchText = defineModel<string>('searchText', { default: '' });
const searchChip = ref<string | null>(null);

// Operators whose value is a list of picks rather than a single value.
const MULTI_OPERATORS = ['in', 'nin'];
const isMultiOperator = (operator?: string | null): boolean => !!operator && MULTI_OPERATORS.includes(operator);

// Accumulates picks while a new multi-value condition is being built; committed
// on menu close (a multi-select has no single "done" pick like single-value does).
const pendingValue = ref<string[]>([]);

const conditionFields = computed<ConditionFieldType[]>(() => {
    return props.fields.map(f => ({
        name: f.name,
        label: f.label,
        operators: f.operators,
    }));
});

const getFieldLabel = (fieldName: string): string => {
    const field = conditionFields.value.find(f => f.name === fieldName);

    return field?.label || fieldName;
};

const getFieldAutocomplete = (fieldName: string): string | undefined => {
    const field = props.fields.find(f => f.name === fieldName);

    return field?.autocomplete;
};

const isEnumField = (fieldName: string): boolean => {
    const field = props.fields.find(f => f.name === fieldName);

    return (
        (field?.type === 'enum' || field?.type === 'boolean') && Array.isArray(field?.values) && field.values.length > 0
    );
};

const getFieldValues = (fieldName: string): string[] => {
    const field = props.fields.find(f => f.name === fieldName);
    if (field?.values && field.values.length > 0) {
        return field.values.map(v => v.label);
    }

    return [];
};

const enumLabelToValue = (fieldName: string, label: string): string => {
    const field = props.fields.find(f => f.name === fieldName);
    if (field?.values) {
        const found = field.values.find(v => v.label === label);
        if (found) {
            return found.value;
        }
    }

    return label;
};

const enumValueToLabel = (fieldName: string, value: string): string => {
    const field = props.fields.find(f => f.name === fieldName);
    if (field?.values) {
        const found = field.values.find(v => v.value === value);
        if (found) {
            return found.label;
        }
    }

    return value;
};

const localEnumValues = ref<string[]>([]);

const currentValueSuggestions = computed(() => {
    if (localEnumValues.value.length > 0) {
        return localEnumValues.value;
    }

    return props.valueSuggestions;
});

const containerRef = ref<InstanceType<typeof ChipInputContainer>>();
const currentStep = ref<ConditionStepType>('field');
const selectedFieldName = ref<string | null>(null);
const selectedOperatorSymbol = ref<string | null>(null);
const editingCondition = ref<string | null>(null);
const autoAdvanceStep = ref<'field' | 'operator' | 'value' | null>(null);
const autoCloseMenu = ref(false);

const conditionsWithStableKeys = computed(() => {
    return conditions.value.map((condition, index) => ({
        ...condition,
        _internal_id: generateInternalId(condition, index),
    }));
});

watch(searchChip, chip => {
    searchText.value = chip || '';
});

onMounted(() => {
    if (searchText.value && searchText.value.trim()) {
        searchChip.value = searchText.value;
    }
});

const checkForOverflow = async () => {
    await containerRef.value?.checkForOverflow();
};

const scrollToActiveInput = async () => {
    await containerRef.value?.scrollToActiveInput();
};

const generateInternalId = (condition: FilterConditionType, index: number): string => {
    // Value is intentionally excluded so the key stays stable while a multi-value
    // condition's array is edited in place (index alone guarantees uniqueness).
    return `${condition.field}_${condition.operator}_${index}`;
};

const findConditionByInternalId = (conditionId: string): { condition: FilterConditionType; index: number } | null => {
    const conditionIndex = conditions.value.findIndex(
        c => generateInternalId(c, conditions.value.indexOf(c)) === conditionId,
    );
    if (conditionIndex !== -1) {
        return {
            condition: conditions.value[conditionIndex],
            index: conditionIndex,
        };
    }

    return null;
};

const handleFieldUpdate = async (conditionId: string, newFieldName: string) => {
    const result = findConditionByInternalId(conditionId);
    if (result) {
        const { index } = result;
        conditions.value[index] = {
            ...conditions.value[index],
            field: newFieldName,
            value: '',
        };
        checkForOverflow();

        if (isEnumField(newFieldName)) {
            localEnumValues.value = getFieldValues(newFieldName);
        } else {
            localEnumValues.value = [];
            if (props.fetchValueSuggestions) {
                await props.fetchValueSuggestions(newFieldName, getFieldAutocomplete(newFieldName));
            }
        }

        const newCondition = conditions.value[index];
        editingCondition.value = generateInternalId(newCondition, index);
        currentStep.value = 'value';
        scrollToActiveInput();
    }
};

const handleOperatorUpdate = (conditionId: string, newOperatorSymbol: string) => {
    const result = findConditionByInternalId(conditionId);
    if (result) {
        const { index } = result;
        conditions.value[index] = {
            ...conditions.value[index],
            operator: newOperatorSymbol,
        };
        checkForOverflow();
    }
};

const mapValueForStorage = (fieldName: string, value: string): string =>
    isEnumField(fieldName) ? enumLabelToValue(fieldName, value) : value;

const handleValueUpdate = (conditionId: string, newValue: string | string[]) => {
    const result = findConditionByInternalId(conditionId);
    if (result) {
        const { index, condition } = result;

        if (isDateRange(condition.field, condition.operator)) {
            conditions.value[index] = {
                ...conditions.value[index],
                value: Array.isArray(newValue) ? newValue : [newValue],
            };
            editingCondition.value = null;
            currentStep.value = 'field';
            checkForOverflow();

            return;
        }

        const multi = isMultiOperator(condition.operator);

        const valueToStore: string | string[] = multi
            ? (Array.isArray(newValue) ? newValue : [newValue]).map(v => mapValueForStorage(condition.field, v))
            : mapValueForStorage(condition.field, Array.isArray(newValue) ? (newValue[0] ?? '') : newValue);
        conditions.value[index] = { ...conditions.value[index], value: valueToStore };
        checkForOverflow();

        // Multi-select keeps the value input open so more picks can be toggled;
        // the edit is closed when the menu closes.
        if (!multi) {
            editingCondition.value = null;
            currentStep.value = 'field';
        }
    }
};

const handleNewValueComplete = (newValue: string | string[]) => {
    if (!selectedFieldName.value || !selectedOperatorSymbol.value) {
        return;
    }

    // Multi-select: accumulate the picks; the condition is committed on menu close.
    if (isMultiOperator(selectedOperatorSymbol.value)) {
        pendingValue.value = Array.isArray(newValue) ? newValue : [newValue];

        return;
    }

    const isRange = isDateRange(selectedFieldName.value, selectedOperatorSymbol.value);
    const singleValue = Array.isArray(newValue) ? (newValue[0] ?? '') : newValue;
    const valueToStore = isRange
        ? Array.isArray(newValue)
            ? newValue
            : [newValue]
        : mapValueForStorage(selectedFieldName.value, singleValue);
    const newCondition: FilterConditionType = {
        field: selectedFieldName.value,
        operator: selectedOperatorSymbol.value,
        value: valueToStore,
    };
    conditions.value.push(newCondition);
    selectedFieldName.value = null;
    selectedOperatorSymbol.value = null;
    currentStep.value = 'field';
    autoAdvanceStep.value = 'field';

    scrollToActiveInput();
};

// Commit the in-progress multi-value condition (built via pendingValue) once the
// user closes the value menu. No-op when nothing was picked.
const commitPendingMultiCondition = (): void => {
    if (selectedFieldName.value && selectedOperatorSymbol.value && pendingValue.value.length > 0) {
        conditions.value.push({
            field: selectedFieldName.value,
            operator: selectedOperatorSymbol.value,
            value: pendingValue.value.map(v => mapValueForStorage(selectedFieldName.value as string, v)),
        });
    }

    pendingValue.value = [];
    selectedFieldName.value = null;
    selectedOperatorSymbol.value = null;
    currentStep.value = 'field';
    autoAdvanceStep.value = 'field';
    checkForOverflow();
};

const handleFieldChipSelect = async (fieldName: string) => {
    selectedFieldName.value = fieldName;
    selectedOperatorSymbol.value = null;
    currentStep.value = 'operator';

    if (isEnumField(fieldName)) {
        localEnumValues.value = getFieldValues(fieldName);
    } else {
        localEnumValues.value = [];
        if (props.fetchValueSuggestions) {
            await props.fetchValueSuggestions(fieldName, getFieldAutocomplete(fieldName));
        }
    }

    await nextTick();
    autoAdvanceStep.value = 'operator';

    scrollToActiveInput();
};

const handleOperatorChipSelect = async (operatorSymbol: string) => {
    selectedOperatorSymbol.value = operatorSymbol;
    pendingValue.value = [];
    currentStep.value = 'value';

    await nextTick();
    autoAdvanceStep.value = 'value';

    scrollToActiveInput();
};

const getOperatorOptions = computed(() => {
    if (!selectedFieldName.value) {
        return [];
    }

    const field = conditionFields.value.find(f => f.name === selectedFieldName.value);

    return field?.operators || [];
});

const getPlaceholderForField = (fieldName: string): string => {
    if (fieldName.endsWith('created_at') || fieldName.endsWith('updated_at')) {
        return 'DD.MM.YYYY';
    }

    return '';
};

const isDateField = (fieldName: string): boolean => {
    return fieldName.endsWith('created_at') || fieldName.endsWith('updated_at');
};

// `btw` on a date field is the only range operator: it needs two inputs and must
// keep its value as a [from, to] pair rather than collapsing to a single string.
const isDateRange = (fieldName: string, operator?: string | null): boolean =>
    operator === 'btw' && isDateField(fieldName);

const getDateInputType = (fieldName: string): 'datetime-local' | 'date' | 'time' => {
    if (fieldName.endsWith('created_at') || fieldName.endsWith('updated_at')) {
        return 'date';
    }

    return 'datetime-local';
};

const getDisplayTextForValue = (value: string | string[], fieldName: string): string => {
    if (Array.isArray(value)) {
        if (isDateField(fieldName)) {
            return value.map(v => formatDateForDisplay(v, getDateInputType(fieldName))).join(' – ');
        }

        // Multi-value conditions show their picks as a comma-separated list.
        return value.map(v => (isEnumField(fieldName) ? enumValueToLabel(fieldName, v) : v)).join(', ');
    }

    if (isDateField(fieldName) && value) {
        const inputType = getDateInputType(fieldName);

        return formatDateForDisplay(value, inputType);
    }

    // For enum fields, convert value to label for display
    if (isEnumField(fieldName)) {
        return enumValueToLabel(fieldName, value);
    }

    return value;
};

const currentPlaceholder = computed(() => {
    if (selectedFieldName.value) {
        return getPlaceholderForField(selectedFieldName.value);
    }

    return '';
});

const handleConditionRemove = (conditionId: string | null) => {
    if (editingCondition.value === conditionId) {
        editingCondition.value = null;
    }

    if (conditionId === null) {
        if (searchChip.value) {
            searchChip.value = null;
            checkForOverflow();
        } else if (conditions.value.length > 0) {
            conditions.value.pop();
            checkForOverflow();
        }
    } else if (conditionId === 'search-chip') {
        searchChip.value = null;
        checkForOverflow();
    } else {
        const result = findConditionByInternalId(conditionId);
        if (result) {
            const { index } = result;
            conditions.value.splice(index, 1);
            checkForOverflow();
        }
    }
};

const handleValueChipClick = async (conditionId: string) => {
    removeIncompleteCondition();
    const result = findConditionByInternalId(conditionId);
    if (result) {
        const { condition } = result;
        editingCondition.value = conditionId;
        currentStep.value = 'value';

        if (isEnumField(condition.field)) {
            localEnumValues.value = getFieldValues(condition.field);
        } else {
            localEnumValues.value = [];
            if (props.fetchValueSuggestions) {
                await props.fetchValueSuggestions(condition.field, getFieldAutocomplete(condition.field));
            }
        }
    }
};

const handleFieldSelect = (fieldName: string) => {
    if (editingCondition.value) {
        handleFieldUpdate(editingCondition.value, fieldName);
        editingCondition.value = null;
        currentStep.value = 'field';
    } else {
        handleFieldChipSelect(fieldName);
    }
};

const handleOperatorSelect = (operatorSymbol: string) => {
    if (editingCondition.value) {
        handleOperatorUpdate(editingCondition.value, operatorSymbol);
        editingCondition.value = null;
        currentStep.value = 'field';
    } else {
        handleOperatorChipSelect(operatorSymbol);
    }
    autoAdvanceStep.value = null;
};

const handleValueSelect = (value: string | string[]) => {
    if (editingCondition.value) {
        handleValueUpdate(editingCondition.value, value);

        // Multi-select stays open for more picks; single-select closes the edit.
        if (!Array.isArray(value)) {
            editingCondition.value = null;
            currentStep.value = 'field';
        }
    } else {
        handleNewValueComplete(value);
    }
};

const handleValueSearch = async (query: string) => {
    if (!selectedFieldName.value) {
        return;
    }

    // For enum fields, local filtering is handled by autocomplete component
    if (isEnumField(selectedFieldName.value)) {
        return;
    }

    if (props.fetchValueSuggestions) {
        await props.fetchValueSuggestions(
            selectedFieldName.value,
            getFieldAutocomplete(selectedFieldName.value),
            query,
        );
    }
};

const handleValueSearchForCondition = async (conditionId: string, query: string) => {
    const result = findConditionByInternalId(conditionId);
    if (!result) {
        return;
    }

    const { condition } = result;

    // For enum fields, local filtering is handled by autocomplete component
    if (isEnumField(condition.field)) {
        return;
    }

    if (props.fetchValueSuggestions) {
        await props.fetchValueSuggestions(condition.field, getFieldAutocomplete(condition.field), query);
    }
};

const handleOperatorChipClick = async (conditionId: string) => {
    removeIncompleteCondition();
    editingCondition.value = conditionId;
    currentStep.value = 'operator';
};

const handleChipFieldClick = (conditionId: string) => {
    removeIncompleteCondition();
    editingCondition.value = conditionId;
    currentStep.value = 'field';
};

const handleMenuToggle = (value: boolean) => {
    autoCloseMenu.value = !value;

    if (!value) {
        // Finish a new multi-value condition being built (picks accumulated in
        // pendingValue) before the generic close handling discards it.
        if (
            !editingCondition.value &&
            selectedFieldName.value &&
            isMultiOperator(selectedOperatorSymbol.value) &&
            pendingValue.value.length > 0
        ) {
            commitPendingMultiCondition();

            return;
        }

        if (editingCondition.value) {
            editingCondition.value = null;
            currentStep.value = 'field';
            autoAdvanceStep.value = null;
        } else {
            currentStep.value = 'field';
            removeIncompleteCondition();
        }
    }
};

const removeIncompleteCondition = () => {
    autoAdvanceStep.value = null;
    selectedFieldName.value = null;
    selectedOperatorSymbol.value = null;
    pendingValue.value = [];
};

const handleClearAllConditions = () => {
    conditions.value = [];
    searchChip.value = null;
    selectedFieldName.value = null;
    selectedOperatorSymbol.value = null;
    pendingValue.value = [];
    currentStep.value = 'field';
    editingCondition.value = null;
};

const handleSearchSelect = (searchText: string) => {
    const trimmed = searchText.trim();
    if (trimmed) {
        searchChip.value = trimmed;
        autoAdvanceStep.value = 'field';
        currentStep.value = 'field';
        checkForOverflow();
    }
};

const handleInputClick = () => {
    if (currentStep.value === 'field' && !editingCondition.value) {
        autoAdvanceStep.value = 'field';
    }
};
</script>

<template>
    <ChipInputContainer
        ref="containerRef"
        :model-value="conditions"
        :clear-all-tooltip="t('common.clear')"
        :show-clear-all="conditions.length > 0 || searchChip !== null"
        :is-active="true"
        :is-filter-container="true"
        @clearAll="handleClearAllConditions"
        @click="handleInputClick"
        @delete-key-pressed="handleConditionRemove(null)"
    >
        <template #placeholder>
            <span />
        </template>

        <div
            v-for="condition in conditionsWithStableKeys"
            :key="condition._internal_id"
            class="token-group d-flex flex-nowrap align-center"
        >
            <ConditionFieldChip
                :data="getFieldLabel(condition.field)"
                :items="conditionFields"
                :loading="loading"
                :mode="editingCondition === condition._internal_id && currentStep === 'field' ? 'input' : 'chip'"
                :auto-open-menu="editingCondition === condition._internal_id && currentStep === 'field'"
                :autofocus="editingCondition === condition._internal_id && currentStep === 'field'"
                size="small"
                @update:menu="handleMenuToggle"
                @click="handleChipFieldClick(condition._internal_id)"
                @select="(newFieldName: string) => handleFieldUpdate(condition._internal_id, newFieldName)"
            />

            <ConditionOperatorChip
                :data="condition.operator"
                :items="conditionFields.find(f => f.name === condition.field)?.operators"
                :loading="loading"
                :mode="editingCondition === condition._internal_id && currentStep === 'operator' ? 'input' : 'chip'"
                :auto-open-menu="editingCondition === condition._internal_id && currentStep === 'operator'"
                :autofocus="editingCondition === condition._internal_id && currentStep === 'operator'"
                size="small"
                @click="handleOperatorChipClick(condition._internal_id)"
                @update:menu="handleMenuToggle"
                @select="(newOperatorSymbol: string) => handleOperatorUpdate(condition._internal_id, newOperatorSymbol)"
            />

            <ConditionDateRangeValueChip
                v-if="isDateRange(condition.field, condition.operator)"
                :mode="editingCondition === condition._internal_id && currentStep === 'value' ? 'input' : 'chip'"
                :data="Array.isArray(condition.value) ? condition.value : []"
                :show-close="true"
                :autofocus="editingCondition === condition._internal_id && currentStep === 'value'"
                size="small"
                @select="(newValue: string[]) => handleValueUpdate(condition._internal_id, newValue)"
                @remove="handleConditionRemove(condition._internal_id)"
                @update:menu="handleMenuToggle"
                @click="handleValueChipClick(condition._internal_id)"
            />

            <ConditionValueChip
                v-else
                :mode="editingCondition === condition._internal_id && currentStep === 'value' ? 'input' : 'chip'"
                :data="condition.value"
                :display-text="getDisplayTextForValue(condition.value, condition.field)"
                :placeholder="getPlaceholderForField(condition.field)"
                :show-close="true"
                :multiple="isMultiOperator(condition.operator)"
                :autofocus="editingCondition === condition._internal_id && currentStep === 'value'"
                :auto-open-menu="editingCondition === condition._internal_id && currentStep === 'value'"
                :items="currentValueSuggestions"
                :loading="suggestionsLoading"
                size="small"
                :is-date-field="isDateField(condition.field)"
                @select="(newValue: string | string[]) => handleValueUpdate(condition._internal_id, newValue)"
                @remove="handleConditionRemove(condition._internal_id)"
                @update:menu="handleMenuToggle"
                @click="handleValueChipClick(condition._internal_id)"
                @search="(query: string) => handleValueSearchForCondition(condition._internal_id, query)"
            />
        </div>

        <BChip
            v-if="searchChip"
            closable
            color="grey-300"
            variant="flat"
            size="small"
            class="search-chip"
            @click:close="handleConditionRemove('search-chip')"
        >
            {{ searchChip }}
        </BChip>

        <ConditionFieldChip
            v-if="selectedFieldName"
            size="small"
            :data="getFieldLabel(selectedFieldName)"
            :items="conditionFields"
        />

        <ConditionOperatorChip
            v-if="selectedOperatorSymbol"
            size="small"
            :data="selectedOperatorSymbol"
            :items="getOperatorOptions"
        />

        <IntelligentFilterInput
            v-if="currentStep === 'field'"
            :fields="props.fields"
            :loading="loading"
            :autofocus="autoAdvanceStep === 'field' && editingCondition === null"
            placeholder="filters.filterOrSearch"
            :auto-close-menu="autoCloseMenu"
            size="small"
            @select-field="handleFieldSelect"
            @select-search="handleSearchSelect"
            @update:menu="handleMenuToggle"
        />

        <ConditionOperatorChip
            v-if="currentStep === 'operator' && autoAdvanceStep === 'operator'"
            mode="input"
            :items="getOperatorOptions"
            :autofocus="autoAdvanceStep === 'operator' && editingCondition === null"
            :auto-open-menu="autoAdvanceStep === 'operator' && editingCondition === null"
            :auto-close-menu="autoCloseMenu"
            size="small"
            @update:menu="handleMenuToggle"
            @select="handleOperatorSelect"
        />

        <ConditionDateRangeValueChip
            v-if="
                currentStep === 'value' &&
                    autoAdvanceStep === 'value' &&
                    selectedFieldName &&
                    isDateRange(selectedFieldName, selectedOperatorSymbol)
            "
            mode="input"
            :autofocus="autoAdvanceStep === 'value' && editingCondition === null"
            size="small"
            @update:menu="handleMenuToggle"
            @select="handleValueSelect"
        />

        <ConditionValueChip
            v-else-if="currentStep === 'value' && autoAdvanceStep === 'value'"
            mode="input"
            :placeholder="currentPlaceholder"
            :data="isMultiOperator(selectedOperatorSymbol) ? pendingValue : undefined"
            :items="currentValueSuggestions"
            :loading="suggestionsLoading"
            :multiple="isMultiOperator(selectedOperatorSymbol)"
            :auto-open-menu="autoAdvanceStep === 'value' && editingCondition === null"
            :autofocus="autoAdvanceStep === 'value' && editingCondition === null"
            :auto-close-menu="autoCloseMenu"
            :is-date-field="selectedFieldName ? isDateField(selectedFieldName) : false"
            size="small"
            @update:menu="handleMenuToggle"
            @select="handleValueSelect"
            @search="handleValueSearch"
        />
    </ChipInputContainer>
</template>

<style lang="scss" scoped>
.token-group .v-chip {
    border-radius: 0;
    border: 2px solid transparent;
    transition: border-color 100ms ease;
}

.token-group .v-chip:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    border-right: 1px solid rgb(var(--v-theme-grey-50));
}

.token-group .v-chip:nth-child(2) {
    border-right: none;
    border-left: none;
}

.token-group .v-chip:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    border-left: 1px solid rgb(var(--v-theme-grey-50));
}

:deep(.token-group:has(.v-chip .v-chip__close:hover) .v-chip) {
    border-color: rgba(var(--v-theme-grey-800));
}
:deep(.token-group:has(.v-chip .v-chip__close:hover) .v-chip:first-child) {
    border-right: 1px solid rgb(var(--v-theme-grey-50));
}
:deep(.token-group:has(.v-chip .v-chip__close:hover) .v-chip:nth-child(2)) {
    border-right: none;
    border-left: none;
}
:deep(.token-group:has(.v-chip .v-chip__close:hover) .v-chip:last-child) {
    border-left: 1px solid rgb(var(--v-theme-grey-50));
}

:deep(.search-chip.v-chip) {
    border-radius: 8px;
    margin-right: 4px;
}

:deep(.token-group) {
    margin-right: 4px;
}

:deep(.search-chip .v-chip__prepend) {
    padding-inline: 8px;
    background: #c5c5c5;
    height: 100%;
    margin-right: 8px;
    color: #000000;
}
</style>
